import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../../ui/dialog";
import Button from "../../button-v1";
import { parseSegmentsWithDateTags } from "../../../utils/parseBoldMarkers";
import {
  contentEditableToMarkerString,
  renderMarkerStringIntoContentEditable,
  insertBreakAtCaret,
  insertTypedTextAsPlain,
} from "../../../utils/contentEditableMarkers";
import DeltaDateTag from "./DeltaDateTag";

// SCRUM-738: replaces NumberedTextArea for care-plan sections that were
// reported as behaving like a bullet list (users could only
// highlight/delete one point at a time instead of selecting freely across
// the whole section - especially painful on Prognosis). This is a single
// auto-growing field instead of one mini-textarea per line, so native
// select-all/delete/cut across the whole field works as expected.
//
// SCRUM-788 bug fix: the editing surface itself is a contentEditable div,
// not a <textarea> - see utils/contentEditableMarkers.js for why (real
// bold + correct native cursor tracking, which an invisible-textarea +
// styled-backdrop-overlay trick could never fully reconcile).
//
// maxLength mimics the browser's own <textarea> cap (onBeforeInput blocks
// further typing/pasting once reached, contentEditable has no native
// equivalent) but does NOT clip an existing value that's already longer
// (e.g. legacy data, or prior-month carryover into a field with a tighter
// limit) - so old over-limit content still displays and saves untouched,
// only new growth is capped. A live counter warns before the user hits the
// wall rather than the field just silently stopping.
//
// showClear renders a top-right "X" (per Devika's note on the ticket) that
// clears just this one section after a confirm dialog - it never touches
// any other field.
const FreeTextSection = ({
  label,
  placeholder = "Enter text here...",
  value = "",
  onChange,
  readOnly = false,
  minHeight = 60,
  maxLength = 5000,
  showClear = true,
  // Optional extra control rendered in the label row, between the label and
  // the clear button (e.g. a "view template reference" icon for a specific
  // field) — a generic slot rather than a field-specific prop, since this
  // component is shared across every long-text section in the care plan.
  headerExtra = null,
}) => {
  const editorRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // contentEditable divs size to their content natively (normal block
  // flow), unlike the <textarea> this replaced - no more manual
  // height:auto+scrollHeight measuring or a ResizeObserver to catch it
  // settling late.
  //
  // Bug fix: this used to compare `value` against a separate "last value we
  // set" ref instead of the DOM itself - reported as fields going visibly
  // blank (while their own character counter, read directly from `value`,
  // still showed the real length) right after clicking "Edit Careplan" on a
  // Completed/Finalized plan. That ref-based cache can only ever be as
  // correct as every code path that's supposed to keep it updated; comparing
  // directly against the DOM's own actual current content instead is
  // self-healing regardless of how a mismatch happened (a transient
  // mount/prop-update ordering edge case, some other future refactor,
  // etc.) - if the DOM doesn't already say what `value` says, this
  // corrects it, full stop.
  useLayoutEffect(() => {
    if (readOnly) return;
    const el = editorRef.current;
    if (!el) return;
    // Skip re-rendering the DOM when it already reflects `value` exactly -
    // this is what makes typing itself not fight this effect: onInput
    // (below) hands the freshly-typed DOM's own content straight to
    // onChange, so by the time this effect re-runs off that same value, the
    // DOM already matches it and live caret position is left completely
    // untouched.
    if (contentEditableToMarkerString(el) === (value || "")) return;
    renderMarkerStringIntoContentEditable(el, value);
  }, [value, readOnly]);

  const length = (value || "").length;
  const nearOrOverLimit = maxLength && length >= maxLength * 0.9;
  const overLimit = maxLength && length > maxLength;
  // Doesn't clip an existing value that's already over the cap (e.g. legacy
  // data, or prior-month carryover into a field with a tighter limit) - old
  // over-limit content still displays and saves untouched, only new growth
  // is blocked, same intent as the <textarea> maxLength attribute this
  // replaced.
  const atMaxLength = maxLength && length >= maxLength;

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    const next = contentEditableToMarkerString(el);
    // Backspacing a field down to nothing often leaves the browser's own
    // placeholder <br> behind, which reads back as "\n" - saved as [""]
    // instead of []. A field with no visible text is empty, exactly as if it
    // had been cleared with the X (handleClear), so report "" in that case.
    onChange && onChange(next.trim() ? next : "");
  };

  // contentEditable has no native maxLength attribute - onBeforeInput fires
  // before the DOM changes (unlike onInput), so it's the one place that can
  // still block a keystroke/paste/IME commit before it lands, matching how
  // the <textarea> this replaced stopped accepting new input at the cap.
  const handleBeforeInput = (e) => {
    if (atMaxLength && e.inputType && e.inputType.startsWith("insert")) {
      e.preventDefault();
    }
  };

  // Typing right after (or inside) AI bold text stays plain - see
  // insertTypedTextAsPlain. A native listener, not React's onBeforeInput:
  // React 17 synthesizes that one from textInput/keypress, which carry no
  // inputType. Re-attached when onChange changes so handleInput never
  // calls a stale one.
  useEffect(() => {
    const el = editorRef.current;
    if (readOnly || !el) return undefined;
    const onNativeBeforeInput = (e) => {
      if (insertTypedTextAsPlain(e, el)) handleInput();
    };
    el.addEventListener("beforeinput", onNativeBeforeInput);
    return () => el.removeEventListener("beforeinput", onNativeBeforeInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, readOnly]);

  // Explicit Enter handling (insertBreakAtCaret) instead of relying on the
  // browser's own inconsistent default contentEditable line-break behavior
  // (wrapping <div>s in some browsers, <br> in others) - same reasoning as
  // the symptom row editor. insertBreakAtCaret mutates the DOM directly, so
  // handleInput is called right after to sync that change into React state,
  // same as it would after any other real keystroke.
  const handleKeyDown = (e) => {
    if (e.nativeEvent?.isComposing || e.key !== "Enter") return;
    e.preventDefault();
    if (atMaxLength) return;
    insertBreakAtCaret();
    handleInput();
  };

  const handleClear = () => {
    onChange && onChange("");
    setConfirmOpen(false);
  };

  if (readOnly) {
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "2px" }}>
        {label && <label style={{ color: "#565975", fontSize: "14px" }}>{label}</label>}
        <div
          style={{
            width: "100%",
            minHeight: `${minHeight}px`,
            height: "auto",
            maxHeight: "none",
            boxSizing: "border-box",
            borderBottom: "1px solid #C9CBD5",
            padding: "9px 12px 9px 0",
            color: "#0D1139",
            fontSize: "14px",
            lineHeight: "19px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {parseSegmentsWithDateTags(value).map((segment, i) =>
            segment.bold ? (
              <strong key={i} style={{ fontWeight: 700, color: "#6b7280" }}>
                {segment.text}
                <DeltaDateTag dateTag={segment.dateTag} />
              </strong>
            ) : (
              <React.Fragment key={i}>{segment.text}</React.Fragment>
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "2px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {label && <label style={{ color: "#0D1139", fontSize: "14px" }}>{label}</label>}
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
          {headerExtra}
          {showClear && value ? (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              title={`Clear ${label || "section"}`}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#94A3B8",
                fontSize: "11px",
                lineHeight: 1,
                padding: "2px 4px",
              }}
            >
              ✕
            </button>
          ) : null}
        </div>
      </div>

      {/* contentEditable-based editor - see utils/contentEditableMarkers.js
          for the full rationale (replaces an earlier invisible-textarea +
          styled-backdrop-overlay trick that could never reconcile real bold
          with correct cursor tracking). Bold is real <strong> elements at
          real weight; the browser's own native caret always tracks the
          actual rendered text correctly as a result. */}
      <div
        style={{
          position: "relative",
          width: "100%",
          minHeight: `${minHeight}px`,
          boxSizing: "border-box",
          border: "1px solid #C9CBD5",
          borderRadius: "6px",
          background: "#F8FAFD",
        }}
      >
        {!value && (
          <span
            style={{
              position: "absolute",
              inset: 0,
              padding: "9px 12px",
              fontSize: "14px",
              lineHeight: "19px",
              fontFamily: "inherit",
              color: "#94A3B8",
              pointerEvents: "none",
            }}
          >
            {placeholder}
          </span>
        )}
        <div
          ref={(el) => {
            editorRef.current = el;
          }}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onBeforeInput={handleBeforeInput}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%",
            minHeight: `${minHeight}px`,
            boxSizing: "border-box",
            padding: "9px 12px",
            outline: "none",
            color: "#565975",
            fontSize: "14px",
            lineHeight: "19px",
            fontFamily: "inherit",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        />
      </div>

      {maxLength ? (
        <span
          style={{
            alignSelf: "flex-end",
            fontSize: "11px",
            color: overLimit ? "#ef4444" : nearOrOverLimit ? "#f59e0b" : "#94A3B8",
          }}
        >
          {length} / {maxLength}
        </span>
      ) : null}

      <Dialog open={confirmOpen} setOpen={setConfirmOpen}>
        <DialogContent style={{ maxWidth: "420px" }}>
          <DialogHeader>
            <DialogTitle>Clear {label || "Section"}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p style={{ color: "#565975", fontSize: "14px", lineHeight: 1.5, margin: 0 }}>
              {label || "This section"} will be cleared — the rest of the care
              plan is not affected. Are you sure you want to proceed?
            </p>
          </DialogBody>
          <DialogFooter style={{ justifyContent: "space-between", gap: "12px" }}>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              style={{ flex: 1, border: "1.5px solid #10164d", color: "#10164d", backgroundColor: "#ffffff" }}
            >
              No
            </Button>
            <Button variant="primary" onClick={handleClear} style={{ flex: 1 }}>
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FreeTextSection;
